import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getISOWeek, getISOWeekYear } from 'date-fns';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import PlanToolbar from './components/PlanToolbar';
import { CommentPopup } from './components/PlanToProduceWeekly';
import { Row } from './components/ProductionPlanGrid';
import { computeForecast, gapValues, gapDaysOf, sortValue, cmpNullsLast } from './utils/productionPlanForecast';
import TypeSettingsModal from './components/TypeSettingsModal';
import PlanVersionsModal from './components/PlanVersionsModal';
import AssignmentsModal from './components/AssignmentsModal';
import DayCommentsPopup from './components/DayCommentsPopup';
import { toMiamiDateString } from './utils/dateUtils';
import './PlanToProducePage.css';
import './ProductionPlanPage.css';

// Production Plan: one grid = fixed product columns + Sales Plan AI (12 months) +
// Demand (12 months) + Days (range, collapsible into ISO weeks and months).
// Data comes as sparse maps from GET /plantoproduce/grid and is aggregated per row.
// AI is weekly ({ ASIN: { 'YYYY-Www': qty } }); the forecast months expand into ISO-week segments
// (share = week × days/7, math in utils/productionPlanForecast.js). Elapsed segments of the current month also show
// the remaining = share − actual sales (data.sales { ASIN: { 'YYYY-MM-DD': units } }, through yesterday).

const STORAGE_KEY = 'pp_view';
const GROUPS = [['sets', 'Sets'], ['wildmicr', 'Wildflowers & Microgreens'], ['fert_products', 'Fertilizers'], ['kit', 'Kits']];
const ZONES = [['ai', 'Sales Plan AI'], ['demand', 'Demand'], ['days', 'Days']];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];   // exports: the calendar is shared with components/PacketsNeedTab.jsx
const EMPTY = [];

const pad2 = n => String(n).padStart(2, '0');
const num = x => Number(x) || 0;
export const addMonths = (ym, n) => { const [y, m] = ym.split('-').map(Number); const t = y * 12 + m - 1 + n; return `${Math.floor(t / 12)}-${pad2(t % 12 + 1)}`; };
const monthDiff = (a, b) => (Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 + Number(b.slice(5)) - Number(a.slice(5));
export const monthLabel = ym => `${MON[Number(ym.slice(5)) - 1]} ${ym.slice(0, 4)}`;
const monthShort = ym => `${MON[Number(ym.slice(5)) - 1]} '${ym.slice(2, 4)}`;
export const dow = d => new Date(d + 'T12:00:00Z').getUTCDay();
const toggle = (arr, x) => arr.includes(x) ? arr.filter(y => y !== x) : [...arr, x];
const isYM = s => /^\d{4}-(0[1-9]|1[0-2])$/.test(s || '');

// Calendar dates of a month as 'YYYY-MM-DD' (UTC arithmetic — no local-TZ Date from ISO strings)
const daysOf = ym => {
  const [y, m] = ym.split('-').map(Number);
  const out = [];
  for (let t = Date.UTC(y, m - 1, 1); t < Date.UTC(y, m, 1); t += 864e5) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
};
const isoWeek = d => {
  const [y, m, dd] = d.split('-').map(Number);
  const dt = new Date(y, m - 1, dd, 12);
  return { n: getISOWeek(dt), key: `${getISOWeekYear(dt)}-W${pad2(getISOWeek(dt))}` };
};

// months → ISO-week segments (a week straddling a month boundary = two segments keyed by the same ISO week) → days
export function buildCal(from, to) {
  const months = [], dayIndex = {};
  if (!isYM(from) || !isYM(to)) return { months, dayIndex };
  for (let m = from; m <= to && months.length < 26; m = addMonths(m, 1)) {
    const segs = [];
    for (const d of daysOf(m)) {
      const wk = isoWeek(d);
      let s = segs[segs.length - 1];
      if (!s || s.week !== wk.key) { s = { week: wk.key, n: wk.n, dates: [] }; segs.push(s); }
      s.dates.push(d);
      dayIndex[d] = { m, w: `${m}|${wk.key}` };
    }
    for (const s of segs) { s.partial = s.dates.length < 7; s.label = `W${pad2(s.n)}${s.partial ? '*' : ''}`; }
    months.push({ m, segs });
  }
  return { months, dayIndex };
}

// Flat column list (drives <colgroup> and row cells) + 4 header rows built together so colSpans always match.
const FIXED = [['asin', 'ASIN', 96], ['sku', 'SKU', 110], ['name', 'Name', 200], ['bpb', 'Qty/box', 64], ['pps', 'Per shift', 60], ['wh', 'WH', 62], ['fba', 'FBA', 62], ['awd', 'AWD', 62]];
const MONTH_SUB = [['Plan', 'Σ Plan − Σ Done, cumulative from the current month (or from the first displayed month when the range starts earlier)'], ['Done', 'Done this month'], ['Dem. Plan', 'Plan + Demand of the month'], ['d Gap', 'Production Gap over the horizon in the box (days after the month end): no production / with plan']];
const GAP_SUM_TIP = 'Planned shifts this month / shifts still needed to cover the shortage over the Gap horizon after the current plan (quantity ÷ per shift; products without "per shift" are skipped)';
const GAP_IN_TIP = 'Gap horizon: days after the month end to cover (30 / 45 / 60 / 90, or "…" to type your own number)';
const GAP_PRESETS = [30, 45, 60, 90];
const PLANNER_TIP = 'Planner View: each month = Demand Plan (cumulative Plan − Done + Demand of the month); no ISO-week expansion';
// Gap horizon box in a month's Gap header: presets + '…' which turns into a number box (Enter / click away applies, empty keeps the old value)
function GapDaysBox({ m, days, onHead }) {
  const [custom, setCustom] = useState(false);
  if (custom) return (
    <input className="pp-gap-in" type="number" min="1" max="365" autoFocus defaultValue={days} title={GAP_IN_TIP}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      onBlur={e => { setCustom(false); onHead({ type: 'gapDays', m, days: Math.round(Number(e.currentTarget.value)) || days }); }} />
  );
  const opts = GAP_PRESETS.includes(days) ? GAP_PRESETS : [...GAP_PRESETS, days].sort((a, b) => a - b);
  return (
    <select className="pp-gap-in" value={days} title={GAP_IN_TIP} onChange={e => (e.target.value === 'other' ? setCustom(true) : onHead({ type: 'gapDays', m, days: Number(e.target.value) }))}>
      {opts.map(d => <option key={d} value={d}>{d}</option>)}
      <option value="other">…</option>
    </select>
  );
}
function buildModel(cal, fcCal, zones, expandedMonths, expandedFcMonths, collapsedWeeks, fbaOpen, awdOpen, today, plannerView, gapDays) {
  const cols = [], r1 = [], r2 = [], r3 = [], r4 = [];
  for (const [k, label, w] of FIXED) {
    cols.push({ k, w });
    const open = k === 'fba' ? fbaOpen : k === 'awd' ? awdOpen : null;
    r1.push({ key: k, label: open === null ? label : `${label} ${open ? '▾' : '▸'}`, rowSpan: 4, cls: k === 'name' ? 'pp-name' : k === 'bpb' ? 'pp-box' : '', act: open === null ? null : { type: k }, title: open === null ? undefined : (open ? 'Hide Available / Inbound' : 'Show Available / Inbound') });
    if (open) {
      cols.push({ k: k + 'A', w: 62 }, { k: k + 'I', w: 62 });
      r1.push({ key: k + 'A', label: 'Avail.', rowSpan: 4 }, { key: k + 'I', label: 'Inbound', rowSpan: 4 });
    }
  }
  for (const [z, label, k] of [['ai', 'Sales Plan AI', 'ai'], ['demand', 'Demand', 'dm']]) {
    if (!zones[z]) continue;
    const zc = `pp-z-${z}`;   // zone tint; pp-zs = thick left border at the zone start
    // Planner View (Demand only): 12 collapsed months, cell = Demand Plan ('mdp'), no week expansion; the th gets the toggle button.
    const planner = z === 'demand' && plannerView;
    const h1 = { key: k, label: <>{planner ? 'Demand · Planner View' : label} <i className="fas fa-eye-slash pp-ico" /></>, colSpan: 0, cls: `pp-zone ${zc} pp-zs`, act: { type: 'zone', z }, planner: z === 'demand', title: (planner ? PLANNER_TIP + ' · ' : '') + 'Hide this section (the buttons above bring it back)' };
    r1.push(h1);
    // Collapsed month = one column (th rowSpan 3); expanded = 'Σ' + one column per ISO-week segment (r3, rowSpan 2).
    // pp-ms separates an expanded month from its neighbours.
    fcCal.months.forEach(({ m, segs }, i) => {
      const open = !planner && expandedFcMonths.includes(m), prevOpen = !planner && i > 0 && expandedFcMonths.includes(fcCal.months[i - 1].m);
      const cls = i === 0 ? `${zc} pp-zs` : open || prevOpen ? `${zc} pp-ms` : zc;
      const act = planner ? null : { type: 'fcMonth', m }, sort = z === 'demand' ? { kind: 'dm', m } : undefined;
      cols.push({ k: planner ? 'mdp' : k, m, w: z === 'demand' ? 68 : 92, cls });   // 68: month label + sort icon at the compact Demand font (11px, 3px padding, 2px icon margin; WebKit clips "▸ May '26" at 66); 92: '9,999 / -9,999' (plan / remaining) measures 80px + 8px padding + border
      h1.colSpan += 1;
      if (planner) { r2.push({ key: k + m, label: monthShort(m), rowSpan: 3, cls, sort, title: 'Demand Plan of the month' }); return; }
      if (!open) { r2.push({ key: k + m, label: '▸ ' + monthShort(m), rowSpan: 3, cls, act, sort, title: 'Show ISO weeks' }); return; }
      r2.push({ key: k + m, label: '▾ ' + monthShort(m), colSpan: 1 + segs.length, cls, act, sort, title: 'Hide ISO weeks' });
      r3.push({ key: k + m, label: 'Σ', rowSpan: 2, cls });
      for (const s of segs) {
        const title = `${s.week}: ${s.dates[0]} – ${s.dates[s.dates.length - 1]} · share = week × ${s.dates.length}/7`;
        cols.push({ k: k + 'w', m, wk: s.week, segKey: `${m}|${s.week}`, w: z === 'demand' ? 52 : 92, cls: `${zc} pp-fcw` });   // AI segments show 'plan / remaining' too
        r3.push({ key: k + m + s.week, label: s.label, rowSpan: 2, cls: `${zc} pp-fcw`, title });
        h1.colSpan += 1;
      }
    });
  }
  // Month separators: the first cell each month pushes to every row gets pp-ms (border),
  // odd months pp-mo (header tint), the first month also pp-zs (zone border).
  const markMonth = (i0, mi) => {
    const add = ' pp-ms' + (mi % 2 ? ' pp-mo' : '') + (mi === 0 ? ' pp-zs' : '');
    for (const [name, arr] of [['cols', cols], ['r1', r1], ['r2', r2], ['r3', r3], ['r4', r4]]) {
      if (arr.length > i0[name]) arr[i0[name]].cls = `${arr[i0[name]].cls || ''}${add}`.trim();
    }
  };
  if (zones.days) for (let mi = 0; mi < cal.months.length; mi++) {
    const { m, segs } = cal.months[mi];
    const i0 = { cols: cols.length, r1: r1.length, r2: r2.length, r3: r3.length, r4: r4.length };
    if (!expandedMonths.includes(m)) {
      cols.push({ k: 'mp', m, w: 60 }, { k: 'md', m, w: 60 }, { k: 'mdp', m, w: 68 }, { k: 'mdf', m, w: 96, cls: 'pp-gap' });   // 96: '−1,200 / 300' at the compact pp-gap font (11px ≈ 6px per digit)
      r1.push({ key: 'm' + m, label: '▸ ' + monthLabel(m), colSpan: 4, act: { type: 'month', m }, title: 'Expand month' });
      r2.push({ key: 'm' + m, label: 'Σ', colSpan: 4, rowSpan: 2 });
      MONTH_SUB.forEach(([label, title], i) => r4.push(i === 3 ? { key: 'm' + m + i, label, title, cls: 'pp-gap', sort: { kind: 'gap', m }, gap: { m, days: gapDaysOf(gapDays, m) } } : { key: 'm' + m + i, label, title }));
      markMonth(i0, mi);
      continue;
    }
    let span = 0;
    for (const s of segs) {
      const wk = `${m}|${s.week}`, collapsed = collapsedWeeks.includes(s.week);
      const part = s.partial ? ` (partial: ${s.dates[0].slice(5)} – ${s.dates[s.dates.length - 1].slice(5)})` : '';
      const title = `${s.week}${part} — click to ${collapsed ? 'expand' : 'collapse'}`;
      if (collapsed) {
        cols.push({ k: 'wp', wk, w: 52 }, { k: 'wd', wk, w: 52 });
        r2.push({ key: wk, label: `▸ ${s.label}`, colSpan: 2, act: { type: 'week', week: s.week }, title });
        r3.push({ key: wk, label: 'Σ', colSpan: 2 });
        r4.push({ key: wk + 'p', label: 'Plan' }, { key: wk + 'd', label: 'Done' });
        span += 2;
        continue;
      }
      r2.push({ key: wk, label: `▾ ${s.label}`, colSpan: s.dates.length * 2, act: { type: 'week', week: s.week }, title });
      for (const d of s.dates) {
        const sun = dow(d) === 0, isToday = d === today, cls = (sun ? 'pp-sun' : '') + (isToday ? ' pp-today' : '');
        cols.push({ k: 'dp', d, cls, w: 52 }, { k: 'dd', d, cls, w: 52 });   // 52px + 4px padding fits '13,200'
        r3.push({ key: d, label: `${DAY[dow(d)]} ${Number(d.slice(8))}`, colSpan: 2, cls, title: d, day: d });
        r4.push({ key: d + 'p', label: 'Plan', cls }, { key: d + 'd', label: 'Done', cls });
        span += 2;
      }
    }
    r1.push({ key: 'm' + m, label: '▾ ' + monthLabel(m), colSpan: span, act: { type: 'month', m }, title: 'Collapse month' });
    markMonth(i0, mi);
  }
  return { cols, rows: [r1, r2, r3, r4], width: cols.reduce((s, c) => s + c.w, 0) };
}

const VIEW_VER = 2;   // 2 (09.09.2026): Seedphony hidden by default — Brand filter starts with Organo Republic only
const defaultView = curYM => ({
  ver: VIEW_VER, range: { from: curYM, to: addMonths(curYM, 11) }, zones: { ai: true, demand: true, days: true },
  expandedMonths: [curYM], expandedFcMonths: [], collapsedWeeks: [], collapsedGroups: [], type: '', searchQuery: '', selectedBrands: ['OR'], selectedNiches: [],
  sort: null, plannerView: false,   // sort: { kind: 'dm' | 'gap', m: 'YYYY-MM', dir: 'asc' | 'desc' } — applied inside each group
  gapDays: {},                      // Gap horizon per month { 'YYYY-MM': days }; missing = 90
});
function loadView(curYM) {
  const def = defaultView(curYM);
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!s || typeof s !== 'object') return def;
    const v = { ...def, ...s, zones: { ...def.zones, ...(s.zones || {}) } };
    if (s.ver !== VIEW_VER) { v.selectedBrands = def.selectedBrands; v.ver = VIEW_VER; }   // one-time reset of the brand filter
    if (!v.range || !isYM(v.range.from) || !isYM(v.range.to) || v.range.from > v.range.to || v.range.to < curYM) v.range = def.range;
    for (const k of ['expandedMonths', 'expandedFcMonths', 'collapsedWeeks', 'collapsedGroups', 'selectedBrands', 'selectedNiches']) if (!Array.isArray(v[k])) v[k] = def[k];
    v.expandedFcMonths = v.expandedFcMonths.filter(isYM);
    const so = v.sort;
    v.sort = so && typeof so === 'object' && ['dm', 'gap'].includes(so.kind) && isYM(so.m) && ['asc', 'desc'].includes(so.dir) ? { kind: so.kind, m: so.m, dir: so.dir } : null;
    v.plannerView = v.plannerView === true;
    v.gapDays = Object.fromEntries(Object.entries(v.gapDays && typeof v.gapDays === 'object' ? v.gapDays : {}).filter(([m, d]) => isYM(m) && Number.isInteger(d) && d > 0 && d <= 365));
    return v;
  } catch { return def; }
}

// Group summary row. Day Plan cell = shifts needed, like the sheet's
// SUM(ARRAYFORMULA(IFERROR(plan / perShift, 0))): products without 'per shift' count as 0.
// Collapsed week = plain sums; collapsed month = cumulative Plan (Σplan − Σdone from the current month,
// or from the first displayed month when the range starts earlier — `cumCal`), Done as % of the month's plan and 90d Gap = planned shifts of the month (Σ plan ÷ per shift,
// the day cells summed) / shifts needed to cover the shortage after the current plan
// (Σ −gapPlan ÷ per shift over products with gapPlan < 0; no 'per shift' → skipped).
function groupSums(rows, data, cal, rowCalc) {
  const shifts = {}, planW = {}, doneW = {}, planM = {}, doneM = {}, cumPlan = {}, gapShifts = {}, planShiftsM = {};
  const add = (map, key, v) => { map[key] = (map[key] || 0) + v; };
  for (const p of rows) {
    const pps = num(p.produce_per_shift), rc = rowCalc[p.SKU];
    if (pps > 0 && rc) for (const { m } of cal.months) { const g = gapValues(rc.fc, rc.cumPlan, m); if (g && g.plan < 0) add(gapShifts, m, -g.plan / pps); }
    for (const [d, q] of Object.entries(data.plan[p.SKU] || {})) {
      const ix = cal.dayIndex[d];
      if (!ix) continue;
      const v = num(q);
      add(planW, ix.w, v); add(planM, ix.m, v);
      if (pps > 0) { add(shifts, d, v / pps); add(planShiftsM, ix.m, v / pps); }
    }
    for (const [d, q] of Object.entries(data.done[p.SKU] || {})) {
      const ix = cal.dayIndex[d];
      if (!ix) continue;
      const v = num(q);
      add(doneW, ix.w, v); add(doneM, ix.m, v);
    }
  }
  let cum = 0;
  for (const { m } of cal.months) { cum += (planM[m] || 0) - (doneM[m] || 0); cumPlan[m] = cum; }
  return { shifts, planW, doneW, planM, doneM, cumPlan, gapShifts, planShiftsM };
}
// Per product: Σplan − Σdone cumulative over the given calendar's months (cumCal: from the current month; the 'Plan' cell of a collapsed month; same formula as groupSums)
function cumPlanOf(planRow, doneRow, cal) {
  const byM = {}, out = {};
  for (const [src, sign] of [[planRow, 1], [doneRow, -1]]) for (const [d, q] of Object.entries(src || {})) { const ix = cal.dayIndex[d]; if (ix) byM[ix.m] = (byM[ix.m] || 0) + sign * num(q); }
  let cum = 0;
  for (const { m } of cal.months) { cum += byM[m] || 0; out[m] = cum; }
  return out;
}
const fmtN = n => n.toLocaleString('en-US');
function sumCell(c, g) {
  switch (c.k) {
    case 'dp': return g.shifts[c.d] ? g.shifts[c.d].toFixed(1) : '';
    case 'wp': return g.planW[c.wk] ? fmtN(g.planW[c.wk]) : '';
    case 'wd': return g.doneW[c.wk] ? fmtN(g.doneW[c.wk]) : '';
    case 'mp': return fmtN(g.cumPlan[c.m] || 0);
    case 'md': return g.planM[c.m] ? `${Math.round((g.doneM[c.m] || 0) / g.planM[c.m] * 100)}%` : '';
    case 'mdf': {   // planned shifts / short shifts
      const pl = g.planShiftsM[c.m] || 0, sh = g.gapShifts[c.m] || 0;
      return pl || sh ? <>{pl.toFixed(1)} / <span className={sh > 0 ? 'pp-rem-neg' : undefined}>{sh.toFixed(1)}</span></> : '';
    }
    default: return '';
  }
}

const errMsg = (err, fallback) => (err.response && err.response.data && err.response.data.error) || fallback;

const ProductionPlanPage = () => {
  const today = toMiamiDateString(new Date());
  const curYM = today.slice(0, 7);
  const [view, setView] = useState(() => loadView(curYM));
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // { sku, date }
  const [comment, setComment] = useState(null);   // { sku, date, top, left }
  const [dayCmts, setDayCmts] = useState({});      // { [day]: [{ id, author, text, created_at, mine }] } — day comments of the range
  const [dayCmt, setDayCmt] = useState(null);      // { day, top, left } — open day-comments popup
  const [fbaOpen, setFbaOpen] = useState(false);
  const [awdOpen, setAwdOpen] = useState(false);
  const [typeSettings, setTypeSettings] = useState({});   // { [type]: { workstations, employees, instruction } }
  const [settingsFor, setSettingsFor] = useState(null);   // group type key whose modal is open
  const [historyOpen, setHistoryOpen] = useState(false);   // plan versions modal
  const [assignOpen, setAssignOpen] = useState(false);     // assignments popup (employees / stations / messages)
  const closeAssign = useCallback(() => setAssignOpen(false), []);
  const dataRef = useRef(null);
  const calcRef = useRef({});     // previous rowCalc: entries are reused while their inputs are unchanged (Row memo hits on edits)
  const navRef = useRef({ skus: [], dates: [] });
  const reqRef = useRef(0);       // drops a stale /grid response after a fast range change
  dataRef.current = data;

  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(view)); } catch {} }, [view]);

  useEffect(() => {
    Promise.all([api.get('/plantoproduce/brands'), api.get('/plantoproduce/niches')])
      .then(([b, n]) => { setBrands(b.data); setNiches(n.data); })
      .catch(err => console.error('Error loading brands/niches:', err));
    api.get('/plantoproduce/type-settings')
      .then(r => setTypeSettings(r.data && typeof r.data === 'object' ? r.data : {}))
      .catch(err => console.error('Error loading type settings:', err));
  }, []);

  const { from, to } = view.range;
  // Data and the cumulative Plan always start at the current month: a range that starts later must still count
  // the plan (and production) between now and its first month (24.09.2026, request from the planners).
  const from0 = from < curYM ? from : curYM;
  const load = useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/plantoproduce/grid', { params: { from: from0, to } });
      if (id !== reqRef.current) return;
      setData(r.data);
    } catch (err) {
      if (id !== reqRef.current) return;
      console.error('Error loading grid:', err);
      setError(errMsg(err, 'Failed to load the production plan'));
    } finally { if (id === reqRef.current) setLoading(false); }
  }, [from0, to]);
  useEffect(() => { load(); }, [load]);

  // Day comments (Days header) for the visible range; kept apart from /grid — they change independently
  useEffect(() => {
    let alive = true;
    api.get('/plantoproduce/day-comments', { params: { from: `${from}-01`, to: daysOf(to).slice(-1)[0] } })
      .then(r => { if (!alive) return; const byDay = {}; for (const c of r.data.rows || []) (byDay[c.day] = byDay[c.day] || []).push(c); setDayCmts(byDay); })
      .catch(err => console.error('Error loading day comments:', err));
    return () => { alive = false; };
  }, [from, to]);
  const onDayCmt = useCallback((day, th) => {
    const r = th.getBoundingClientRect();
    setDayCmt({ day, top: Math.min(r.bottom + 4, window.innerHeight - 360), left: Math.min(r.left, window.innerWidth - 310) });
  }, []);
  const postDayCmt = async text => {
    const { day } = dayCmt;
    try {
      const r = await api.post('/plantoproduce/day-comments', { day, text });
      setDayCmts(s => ({ ...s, [day]: [...(s[day] || []), r.data] }));
    } catch (err) { console.error('Error posting day comment:', err); setError(errMsg(err, `Failed to post the comment for ${day}`)); }
  };
  const deleteDayCmt = async id => {
    const { day } = dayCmt;
    try {
      await api.delete(`/plantoproduce/day-comments/${id}`);
      setDayCmts(s => ({ ...s, [day]: (s[day] || []).filter(c => c.id !== id) }));
    } catch (err) { console.error('Error deleting day comment:', err); setError(errMsg(err, 'Failed to delete the comment')); }
  };

  const patch = useCallback(p => setView(v => ({ ...v, ...p })), []);
  const setters = useMemo(() => {
    const mk = k => val => setView(v => ({ ...v, [k]: typeof val === 'function' ? val(v[k]) : val }));
    return { searchQuery: mk('searchQuery'), selectedBrands: mk('selectedBrands'), selectedNiches: mk('selectedNiches') };
  }, []);
  const clearFilters = useCallback(() => patch({ searchQuery: '', selectedBrands: [], selectedNiches: [], type: '' }), [patch]);
  const toggleGroup = t => setView(v => ({ ...v, collapsedGroups: toggle(v.collapsedGroups, t) }));
  const closeSettings = useCallback(() => setSettingsFor(null), []);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);
  const onRestored = useCallback(() => { setHistoryOpen(false); load(); }, [load]);
  const onSettingsSaved = useCallback((t, vals) => { setTypeSettings(s => ({ ...s, [t]: { ...(s[t] || {}), ...vals } })); setSettingsFor(null); }, []);

  const fcCal = useMemo(() => buildCal(curYM, addMonths(curYM, 11)), [curYM]);   // forecast zones: 12 months from the current one
  const monthOptions = useMemo(() => Array.from({ length: 43 }, (_, i) => addMonths(curYM, i - 24)), [curYM]);
  const cal = useMemo(() => buildCal(from, to), [from, to]);
  const cumCal = useMemo(() => (from0 === from ? cal : buildCal(from0, to)), [from0, from, to, cal]);   // cumulative Plan / Σ row: current month → range end
  const model = useMemo(
    () => buildModel(cal, fcCal, view.zones, view.expandedMonths, view.expandedFcMonths, view.collapsedWeeks, fbaOpen, awdOpen, today, view.plannerView, view.gapDays),
    [cal, fcCal, view.zones, view.expandedMonths, view.expandedFcMonths, view.collapsedWeeks, fbaOpen, awdOpen, today, view.plannerView, view.gapDays]
  );

  const products = data ? data.products : EMPTY;
  const { searchQuery, type, selectedBrands, selectedNiches, plannerView } = view;
  // Effective sort: view.sort is kept, but only applied while its column is in the header (Days month expanded / zone hidden / range moved → no sort, no icon)
  const sort = view.sort && model.rows.some(r => r.some(h => h.sort && h.sort.kind === view.sort.kind && h.sort.m === view.sort.m)) ? view.sort : null;
  // { [SKU]: { fc, cumPlan } } — forecast + cumulative plan per product, computed once for the rows and the sort
  const gapDays = view.gapDays;
  const rowCalc = useMemo(() => {
    const prev = calcRef.current, next = {};
    if (data) for (const p of data.products) {
      const planRow = data.plan[p.SKU], doneRow = data.done[p.SKU], aiRow = p.ASIN ? data.ai[p.ASIN] : undefined, salesRow = p.ASIN && data.sales ? data.sales[p.ASIN] : undefined, o = prev[p.SKU];
      next[p.SKU] = o && o.p === p && o.planRow === planRow && o.doneRow === doneRow && o.aiRow === aiRow && o.salesRow === salesRow && o.today === today && o.cumCal === cumCal && o.fcCal === fcCal && o.gapDays === gapDays ? o
        : { p, planRow, doneRow, aiRow, salesRow, today, cumCal, fcCal, gapDays, fc: computeForecast(fcCal, aiRow, num(p.stock_wh) + num(p.stock_fba) + num(p.stock_awd), salesRow, today, gapDays), cumPlan: cumPlanOf(planRow, doneRow, cumCal) };
    }
    calcRef.current = next;
    return next;
  }, [data, cumCal, fcCal, today, gapDays]);
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return products.filter(p =>
      (!q || p.name?.toLowerCase().includes(q) || p.SKU?.toLowerCase().includes(q) || p.ASIN?.toLowerCase().includes(q)) &&
      (!type || p.type === type) &&
      (selectedBrands.length === 0 || selectedBrands.includes(String(p.brandID))) &&
      (selectedNiches.length === 0 || selectedNiches.includes(String(p.niche_category_id)))
    );
  }, [products, searchQuery, type, selectedBrands, selectedNiches]);
  const sortRows = useCallback(rows => {   // stable sort inside a group; sort = null keeps the server order
    if (!sort) return rows;
    const cmp = cmpNullsLast(sort.dir), key = p => (rowCalc[p.SKU] ? sortValue(rowCalc[p.SKU].fc, rowCalc[p.SKU].cumPlan, sort, plannerView) : null);
    return rows.map(p => [key(p), p]).sort((a, b) => cmp(a[0], b[0])).map(x => x[1]);
  }, [sort, plannerView, rowCalc]);
  const groups = useMemo(() => GROUPS.map(([t, label]) => ({ t, label, rows: sortRows(filtered.filter(p => p.type === t)) })).filter(g => g.rows.length), [filtered, sortRows]);
  const sums = useMemo(() => (data ? groups.map(g => groupSums(g.rows, data, cumCal, rowCalc)) : []), [groups, data, cumCal, rowCalc]);
  navRef.current = { skus: groups.filter(g => !view.collapsedGroups.includes(g.t)).flatMap(g => g.rows.map(p => p.SKU)), dates: model.cols.filter(c => c.k === 'dp').map(c => c.d) };

  const setRange = (key, val) => setView(v => {
    let { from: f, to: t } = { ...v.range, [key]: val };
    if (f > t) { if (key === 'from') t = f; else f = t; }
    if (monthDiff(f, t) > 23) { if (key === 'from') t = addMonths(f, 23); else f = addMonths(t, -23); }
    return { ...v, range: { from: f, to: t } };
  });

  const onHead = useCallback(act => {
    if (act.type === 'fba') setFbaOpen(o => !o);
    else if (act.type === 'awd') setAwdOpen(o => !o);
    else if (act.type === 'zone') setView(v => ({ ...v, zones: { ...v.zones, [act.z]: !v.zones[act.z] } }));
    else if (act.type === 'month') setView(v => ({ ...v, expandedMonths: toggle(v.expandedMonths, act.m) }));
    else if (act.type === 'fcMonth') setView(v => ({ ...v, expandedFcMonths: toggle(v.expandedFcMonths, act.m) }));
    else if (act.type === 'week') setView(v => ({ ...v, collapsedWeeks: toggle(v.collapsedWeeks, act.week) }));
    else if (act.type === 'planner') setView(v => ({ ...v, plannerView: !v.plannerView }));
    else if (act.type === 'gapDays') setView(v => {   // 90 (the default) and junk are stored as "no override"
      const g = { ...v.gapDays };
      if (Number.isInteger(act.days) && act.days > 0 && act.days <= 365 && act.days !== 90) g[act.m] = act.days; else delete g[act.m];
      return { ...v, gapDays: g };
    });
    else if (act.type === 'sort') setView(v => {   // same column: asc → desc → none; another column: asc
      const s = v.sort, same = s && s.kind === act.kind && s.m === act.m;
      return { ...v, sort: !same ? { kind: act.kind, m: act.m, dir: 'asc' } : s.dir === 'asc' ? { ...s, dir: 'desc' } : null };
    });
  }, []);
  const sortIcon = s => (sort && sort.kind === s.kind && sort.m === s.m ? `fa-sort-${sort.dir === 'asc' ? 'up' : 'down'} on` : 'fa-sort');

  const onEdit = useCallback((sku, date) => setEditing({ sku, date }), []);
  // value === null → cancel; move: 'down' | 'next' | 'prev' | null. Optimistic write, revert + banner on failure.
  const onEditDone = useCallback((sku, date, value, move) => {
    if (value !== null) {
      const qty = value.trim() === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
      const d = dataRef.current;
      const prev = num(d && d.plan[sku] && d.plan[sku][date]);
      if (qty !== prev) {
        const put = q => setData(s => ({ ...s, plan: { ...s.plan, [sku]: { ...(s.plan[sku] || {}), [date]: q } } }));
        put(qty);
        api.post('/plantoproduce/daily-plan', { sku, date, qty_to_produce: qty })
          .catch(err => { console.error('Error saving daily plan:', err); put(prev); setError(`Failed to save ${sku} ${date} — value reverted`); });
      }
    }
    let next = null;
    if (move) {
      const { skus, dates } = navRef.current;
      if (move === 'down') { const i = skus.indexOf(sku) + 1; if (i > 0 && i < skus.length) next = { sku: skus[i], date }; }
      else { const i = dates.indexOf(date) + (move === 'next' ? 1 : -1); if (i >= 0 && i < dates.length) next = { sku, date: dates[i] }; }
    }
    setEditing(next);
  }, []);

  const onComment = useCallback((sku, date, td) => {
    const r = td.getBoundingClientRect();
    setComment({ sku, date, top: Math.min(r.bottom + 4, window.innerHeight - 230), left: Math.min(r.left, window.innerWidth - 310) });
  }, []);
  const saveComment = text => {
    const { sku, date } = comment;
    setComment(null);
    setData(s => ({ ...s, comments: { ...s.comments, [sku]: { ...(s.comments[sku] || {}), [date]: text } } }));
    api.post('/plantoproduce/daily-comment', { sku, date, comment: text })
      .catch(err => { console.error('Error saving comment:', err); setError(`Failed to save the comment for ${sku} ${date}`); });
  };

  const hasActiveFilters = !!(searchQuery || type || selectedBrands.length || selectedNiches.length);
  const fixedSpan = 4; // ASIN, SKU, Name, Qty/box; group/Σ rows: ASIN+SKU plain cell + Name+Qty/box sticky label (= the frozen pane width)
  const wsStaff = t => {   // 'WS n · Staff m' from the type settings, '' when neither is set
    const s = typeSettings[t] || {};
    return [s.workstations != null && `WS ${s.workstations}`, s.employees != null && `Staff ${s.employees}`].filter(Boolean).join(' · ');
  };

  return (
    <div className="pp-root pp-fill">
      <HeaderLogged />
      <div className="ptp-container pp-main">
        <div className="ptp-page-header">
          <div>
            <h2 className="ptp-title">Production Plan</h2>
            <p className="ptp-subtitle">Sales Plan AI · Demand · Daily production plan</p>
          </div>
        </div>

        <PlanToolbar
          searchQuery={searchQuery} setSearchQuery={setters.searchQuery}
          selectedBrands={selectedBrands} setSelectedBrands={setters.selectedBrands}
          selectedNiches={selectedNiches} setSelectedNiches={setters.selectedNiches}
          brands={brands} niches={niches}
          filteredCount={filtered.length} totalCount={products.length}
          hasActiveFilters={hasActiveFilters} clearFilters={clearFilters}
          activeTab="monthly"
        />

        <div className="pp-bar">
          <select value={type} onChange={e => patch({ type: e.target.value })}>
            <option value="">All types</option>
            {GROUPS.map(([t, l]) => <option key={t} value={t}>{l}</option>)}
          </select>
          <span>From</span>
          <select value={from} onChange={e => setRange('from', e.target.value)}>
            {monthOptions.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          <span>To</span>
          <select value={to} onChange={e => setRange('to', e.target.value)}>
            {monthOptions.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          <span className="pp-sep" />
          {ZONES.map(([z, l]) => (
            <button key={z} className={`pp-tgl${view.zones[z] ? ' on' : ''}`} onClick={() => onHead({ type: 'zone', z })}>{l}</button>
          ))}
          <button className={`pp-tgl${plannerView ? ' on' : ''}`} onClick={() => onHead({ type: 'planner' })} title={PLANNER_TIP}>Planner View</button>
          <span className="pp-sep" />
          <button className="pp-tgl" onClick={() => patch({ expandedMonths: [] })}>Collapse all months</button>
          <button className="pp-tgl" onClick={() => patch({ expandedMonths: view.expandedMonths.includes(curYM) ? view.expandedMonths : [...view.expandedMonths, curYM] })}>Expand current month</button>
          <span className="pp-sep" />
          <button className={`pp-tgl${historyOpen ? ' on' : ''}`} onClick={() => setHistoryOpen(true)} title="Saved versions of the plan: save / restore"><i className="fas fa-history"></i> History</button>
          <button className={`pp-tgl${assignOpen ? ' on' : ''}`} onClick={() => setAssignOpen(true)} title="Day assignments: employees, stations, messages"><i className="fas fa-users"></i> Assignments</button>
          {loading && <span className="pp-muted"><i className="fas fa-circle-notch spin"></i> Loading…</span>}
        </div>

        {error && (
          <div className="ptp-load-error">
            <i className="fas fa-exclamation-triangle"></i> {error}
            <button className="pp-x" onClick={() => setError(null)} title="Dismiss">×</button>
          </div>
        )}

        {/* ponytail: no windowing; rows are fixed 28px — add a scrollTop row window if a month toggle measures > 600 ms */}
        <div className="pp-wrap">
          <table className="pp-table" style={{ width: model.width }}>
            <colgroup>{model.cols.map((c, i) => <col key={i} style={{ width: c.w }} />)}</colgroup>
            <thead>
              {model.rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map(h => (
                    <th key={h.key} colSpan={h.colSpan} rowSpan={h.rowSpan} title={h.title}
                        className={`${h.cls || ''}${h.act ? ' pp-click' : ''}`}
                        onClick={h.act ? () => onHead(h.act) : undefined}>
                      {h.gap && <GapDaysBox m={h.gap.m} days={h.gap.days} onHead={onHead} />}
                      {h.label}
                      {h.day && (
                        <button className={`pp-daycmt${dayCmts[h.day]?.length ? ' has' : ''}`} title={dayCmts[h.day]?.length ? `${dayCmts[h.day].length} comment(s) — click to view` : 'Add a comment for this day'}
                          onClick={e => { e.stopPropagation(); onDayCmt(h.day, e.currentTarget.closest('th')); }}>
                          <i className="fas fa-comment" />{dayCmts[h.day]?.length > 0 && <span>{dayCmts[h.day].length}</span>}
                        </button>
                      )}
                      {h.sort && <i className={`fas ${sortIcon(h.sort)} pp-sort`} title="Sort by this column" onClick={e => { e.stopPropagation(); onHead({ type: 'sort', ...h.sort }); }} />}
                      {h.planner && <button className={`pp-tgl-sm${plannerView ? ' on' : ''}`} title={PLANNER_TIP} onClick={e => { e.stopPropagation(); onHead({ type: 'planner' }); }}>Planner View</button>}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr><td className="pp-empty" colSpan={model.cols.length}>{loading ? 'Loading…' : data ? 'No products match your filters' : ''}</td></tr>
              )}
              {groups.map((g, gi) => {
                const collapsed = view.collapsedGroups.includes(g.t);
                return (
                <React.Fragment key={g.t}>
                  <tr className="pp-group">
                    <td colSpan={2} />
                    <td className="pp-glabel" colSpan={2}>
                      {g.label} <span className="pp-muted">({g.rows.length})</span>
                      <button className="pp-gbtn" title={collapsed ? 'Expand products' : 'Collapse products'} onClick={() => toggleGroup(g.t)}>{collapsed ? '+' : '−'}</button>
                      <button className="pp-gbtn" title={wsStaff(g.t) ? `Type settings (${wsStaff(g.t)})` : 'Type settings'} onClick={() => setSettingsFor(g.t)}><i className="fas fa-cog"></i></button>
                    </td>
                    <td colSpan={model.cols.length - fixedSpan} />
                  </tr>
                  {!collapsed && g.rows.map(p => (
                    <Row key={p.SKU} p={p} cols={model.cols} cal={cal} fc={rowCalc[p.SKU].fc} cumPlan={rowCalc[p.SKU].cumPlan}
                         planRow={data.plan[p.SKU]} doneRow={data.done[p.SKU]} commentRow={data.comments[p.SKU]}
                         editing={editing && editing.sku === p.SKU ? editing : null}
                         onEdit={onEdit} onEditDone={onEditDone} onComment={onComment} />
                  ))}
                  <tr className="pp-sum" title="Day Plan: shifts needed (Σ plan ÷ per shift) · week: sums · month: cumulative Plan, Done % of plan, Gap: planned shifts / shifts still needed to cover the shortage over the Gap horizon">
                    <td colSpan={2} />
                    <td className="pp-glabel" colSpan={2}>Σ</td>
                    {model.cols.slice(fixedSpan).map((c, i) => {
                      const v = sums[gi] ? sumCell(c, sums[gi]) : '';
                      return <td key={i} className={`${c.cls || ''}${c.k === 'mp' && (sums[gi] ? sums[gi].cumPlan[c.m] : 0) < 0 ? ' pp-neg' : ''}`} title={c.k === 'mdf' ? GAP_SUM_TIP : undefined}>{v}</td>;
                    })}
                  </tr>
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {settingsFor && (
        <TypeSettingsModal key={settingsFor} type={settingsFor} label={(GROUPS.find(([t]) => t === settingsFor) || [])[1] || settingsFor}
                           initial={typeSettings[settingsFor]} onClose={closeSettings} onSaved={onSettingsSaved} />
      )}
      {historyOpen && <PlanVersionsModal onClose={closeHistory} onRestored={onRestored} />}
      {assignOpen && <AssignmentsModal onClose={closeAssign} />}
      {dayCmt && (
        <>
          <div className="pp-comment-overlay" onClick={() => setDayCmt(null)} />
          <div className="ptp-comment-popup-wrapper" style={{ position: 'fixed', top: dayCmt.top, left: dayCmt.left, zIndex: 501 }}>
            <DayCommentsPopup day={dayCmt.day} comments={dayCmts[dayCmt.day] || []} onPost={postDayCmt} onDelete={deleteDayCmt} onClose={() => setDayCmt(null)} />
          </div>
        </>
      )}
      {comment && (
        <>
          <div className="pp-comment-overlay" onClick={() => setComment(null)} />
          <div className="ptp-comment-popup-wrapper" style={{ position: 'fixed', top: comment.top, left: comment.left, zIndex: 501 }}>
            <CommentPopup
              comment={data.comments[comment.sku] && data.comments[comment.sku][comment.date]}
              onSave={saveComment}
              onClose={() => setComment(null)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ProductionPlanPage;
